<template>
  <div class="space-y-6">
    <!-- Dataset Type -->
    <div
      class="bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50 rounded-2xl p-3 md:p-6 flex flex-col gap-6 shadow-sm"
    >
      <div>
        <label class="block mb-2">
          <div class="flex items-center gap-2 font-semibold">
            <span>Dataset Type</span>

            <!-- Tooltip only on icon hover -->
            <div class="relative group inline-block">
              <Icon
                icon="mdi:information-outline"
                class="w-4 h-4 text-gray-400 hover:text-mocha-mousse cursor-help transition-colors"
              />
              <div
                class="absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl z-50"
              >
                Select the type of file your dataset is used for.
                <strong>Test</strong> is used only for demo uploads.
              </div>
            </div>
          </div>
        </label>

        <!-- Select -->
        <select
          v-model="formState.upload_type"
          @change="onUploadTypeChange"
          class="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl font-medium transition-all focus:border-mocha-mousse-500 focus:ring-4 focus:ring-mocha-mousse-100 focus:outline-none hover:border-gray-300"
          :class="[
            insertionTypeIsInvalid() && formChecked
              ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
              : '',
          ]"
        >
          <option value="Test">🧪 Test</option>
          <option value="Manuscript">📄 Manuscript data</option>
          <option value="Project">📁 Project files</option>
        </select>

        <p
          v-if="insertionTypeIsInvalid() && formChecked"
          class="mt-2 text-sm text-red-600 flex items-center gap-1"
        >
          <Icon icon="mdi:alert-circle" class="w-4 h-4" />
          Dataset type is required
        </p>
      </div>

      <!-- Title -->

      <div>
        <label class="block mb-2">
          <div class="flex items-center gap-2 font-semibold">
            <span>Title</span>

            <!-- Tooltip only triggers when hovering icon -->
            <div class="relative group inline-block">
              <Icon
                icon="mdi:information-outline"
                class="w-4 h-4 text-gray-400 hover:text-mocha-mousse cursor-help transition-colors"
              />
              <div
                class="absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl z-50"
              >
                Insert the title of the dataset. The complete title is the
                concatenation of
                <strong>“Dataset of”</strong> and the string you type.
              </div>
            </div>
          </div>
        </label>

        <!-- Input -->
        <div
          class="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden transition-all focus-within:border-mocha-mousse-500 focus-within:ring-4 focus-within:ring-mocha-mousse-100 hover:border-gray-300"
          :class="[
            titleIsInvalid() && formChecked
              ? 'border-red-500 focus-within:border-red-500 focus-within:ring-red-100'
              : '',
          ]"
        >
          <div
            class="px-4 py-3 bg-gradient-to-r from-spicy-mustard-200 to-mocha-mousse-100"
          >
            <span class="font-medium text-mocha-mousse-700 whitespace-nowrap">
              Dataset of
            </span>
          </div>

          <input
            v-model="formState.title"
            @change="onTitleChange"
            type="text"
            placeholder="Enter dataset title..."
            class="flex-1 px-4 py-3 bg-white focus:outline-none font-medium"
          />
        </div>

        <!-- Error -->
        <p
          v-if="titleIsInvalid() && formChecked"
          class="mt-2 text-sm text-red-600 flex items-center gap-1"
        >
          <Icon icon="mdi:alert-circle" class="w-4 h-4" />
          Title is required
        </p>
      </div>

      <!-- Open Dataset Toggle -->
      <div>
        <label class="block mb-2">
          <div class="flex items-center gap-2 font-semibold">
            <span>Open Dataset</span>

            <!-- Tooltip on icon only -->
            <div class="relative group inline-block">
              <Icon
                icon="mdi:information-outline"
                class="w-4 h-4 text-gray-400 hover:text-mocha-mousse cursor-help transition-colors"
              />
              <div
                class="absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 shadow-xl z-50"
              >
                Click the toggle to change the dataset visibility (Open or
                Closed).
              </div>
            </div>
          </div>
        </label>

        <!-- Toggle -->
        <label
          class="relative inline-flex items-center cursor-pointer group/toggle"
        >
          <input
            v-model="formState.is_open"
            @change="onOpenDatasetChange"
            type="checkbox"
            class="sr-only peer"
          />
          <div
            class="w-14 h-7 bg-gradient-to-r from-red-200 to-red-400 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-mocha-mousse-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-green-200 peer-checked:to-green-400 shadow-lg"
          ></div>

          <span class="ml-4 text-sm font-medium flex items-center gap-2">
            Publicly available as
            <span
              class="font-bold px-2 py-0.5 rounded-lg transition-colors flex items-center gap-1"
              :class="
                formState.is_open
                  ? 'text-green-600 bg-green-100'
                  : 'text-red-600 bg-red-100'
              "
            >
              <Icon
                :icon="
                  formState.is_open
                    ? 'mdi:lock-open-variant'
                    : 'mdi:lock-outline'
                "
                class="w-4 h-4"
              />
              {{ formState.is_open ? "Open" : "Closed" }}
            </span>
          </span>
        </label>
      </div>
    </div>

    <!-- File Uploaders -->
    <div>
      <UploadersComponent ref="UploaderComp" />
    </div>
    <div
      class="bg-gradient-to-br from-spicy-mustard-100 to-mocha-mousse-50 rounded-2xl p-3 md:p-6 shadow-sm"
    >
      <h3 class="font-semibold mb-4 flex items-center gap-2">
        <Icon icon="mdi:cloud-upload" class="w-5 h-5 text-mocha-mousse" />
        Upload Files
      </h3>

      <!-- File and Folder Loader -->
      <FileAndFolderLoader ref="FileComp" />
    </div>
    <!-- Next Button -->
    <div class="flex justify-end pt-4">
      <button
        @click="goToNextPage"
        :disabled="isLoading"
        class="group relative px-8 py-3 cursor-pointer bg-gradient-to-r from-mint-green-100 to-periwinkle-100 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
      >
        <span
          class="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity"
        ></span>
        <span class="relative flex items-center gap-2">
          Next
          <Icon
            icon="mdi:arrow-right"
            class="w-5 h-5 group-hover:translate-x-1 transition-transform"
          />
        </span>
      </button>
    </div>
  </div>
</template>

<script setup>
import UploadersComponent from "../components/UploadersComponent.vue";
import FileAndFolderLoader from "../components/FileAndFolderLoader.vue";
import { ref, computed, onBeforeMount } from "vue";
import { useRouter } from "vue-router";
import { useFormStore } from "@/stores/FormStore";
import { useLogger } from "@/stores/useLogger";
import { Icon } from "@iconify/vue";
import authService from "@/services/authService";

const logger = useLogger();
logger.wireLifecycle();

const router = useRouter();
const formStore = useFormStore();
const formState = formStore.formState;

const formChecked = ref(false);
const UploaderComp = ref(null);
const FileComp = ref(null);

// reflect child loader
const isLoading = computed(
  () => !!(FileComp.value && FileComp.value.loadingData),
);

// default upload_type via store API
onBeforeMount(() => {
  if (!formState.upload_type) {
    formStore.patch({ upload_type: "Test" });
  }

  // Set logged-in user as the lead uploader
  const user = authService.currentUser.value;
  if (user && user.vuo_id) {
    formStore.setLeadUploader({
      id: user.vuo_id,
      name: user.name || "",
      lastname: user.lastname || "",
      email: user.vuo_email || user.email || "",
      institute: user.institute || "",
    });
  }
});

const ALLOWED_TYPES = ["Test", "Manuscript", "Project"];

// validations
const titleIsInvalid = () => !formStore.isTitleValid; // store-derived
const insertionTypeIsInvalid = () =>
  !ALLOWED_TYPES.includes(formState.upload_type || "");

// page-level validation (plus child components)
const validateInputs = async () => {
  formChecked.value = true;
  let isValid = true;

  if (insertionTypeIsInvalid()) isValid = false;
  if (titleIsInvalid()) isValid = false;
  if (!(await UploaderComp.value.validateInput())) isValid = false;
  if (!FileComp.value.validateInput()) isValid = false;

  return isValid;
};

const goToNextPage = async () => {
  logger.log("button_clicked", { metadata: { button: "to_extra" } });
  if (await validateInputs()) {
    router.push({ name: "extra" });
  }
};

// handlers — patch through the store (keeps persistence/flags consistent)
function onUploadTypeChange(event) {
  const value = event.target.value;
  formStore.patch({ upload_type: value });
  logger.log("upload_type_changed", { metadata: { newValue: value } });
}

function onTitleChange(event) {
  const value = event.target.value ?? "";
  formStore.patch({ title: value, preview_needs_refresh: true });
  logger.log("upload_title_changed", { metadata: { newValue: value } });
  // optionally normalize on blur elsewhere: formStore.sanitizeTitle()
}

function onOpenDatasetChange(event) {
  const checked = !!event.target.checked;
  formStore.patch({ is_open: checked });
  logger.log("upload_open_dataset_changed", {
    metadata: { newValue: checked },
  });
}
</script>
